/**
 * Mappings for prompt outputs to UI display values
 * Handles both Polish prompt outputs and English legacy values
 */

// =============================================================================
// TODO LIST MAPPINGS
// =============================================================================

export interface PriorityMapping {
  label: string;
  color: 'red' | 'amber' | 'orange' | 'gray' | 'green';
  description?: string;
  legacy?: boolean;
}

export const PRIORITY_MAPPING: Record<string, PriorityMapping> = {
  // Polish Eisenhower Matrix (from prompt)
  'Pilne+Ważne': {
    label: 'Pilne+Ważne',
    color: 'red',
    description: 'Zadania z bliskim terminem + wysokim wpływem na sprzedaż'
  },
  'Ważne+Niepilne': {
    label: 'Ważne+Niepilne', 
    color: 'amber',
    description: 'Strategiczne zadania długoterminowe'
  },
  'Pilne+Nieważne': {
    label: 'Pilne+Nieważne',
    color: 'orange', 
    description: 'Szybkie akcje o niskim wpływie'
  },
  'Niepilne+Nieważne': {
    label: 'Niepilne+Nieważne',
    color: 'gray',
    description: 'Zadania nice-to-have'
  },
  
  // Legacy English values (backward compatibility)
  'High': {
    label: 'Pilne+Ważne',
    color: 'red',
    legacy: true
  },
  'Medium': {
    label: 'Ważne+Niepilne',
    color: 'amber', 
    legacy: true
  },
  'Low': {
    label: 'Pilne+Nieważne',
    color: 'orange',
    legacy: true
  }
} as const;

export interface CategoryMapping {
  label: string;
  color: 'purple' | 'indigo' | 'cyan' | 'green' | 'teal';
  description?: string;
}

export const CATEGORY_MAPPING: Record<string, CategoryMapping> = {
  'IMMEDIATE FOLLOW-UP': {
    label: 'Natychmiastowe follow-up',
    color: 'purple',
    description: 'Email podsumowujący, dziękujący, wysłanie materiałów'
  },
  'PREPARATION': {
    label: 'Przygotowanie',
    color: 'indigo',
    description: 'Przygotowanie ofert, propozycji, demo, prezentacji'
  },
  'INTERNAL COORDINATION': {
    label: 'Koordynacja wewnętrzna',
    color: 'cyan', 
    description: 'Konsultacje z zespołem, uzyskanie zatwierdzeń'
  },
  'CLIENT ACTIONS': {
    label: 'Działania klienta',
    color: 'green',
    description: 'Decyzje klienta, dostarczenie informacji, zatwierdzenia'
  },
  'MEETING MANAGEMENT': {
    label: 'Zarządzanie spotkaniami',
    color: 'teal',
    description: 'Organizacja kolejnych spotkań, koordynacja kalendarzy'
  }
} as const;

// =============================================================================
// EMAIL COMMUNICATION MAPPINGS  
// =============================================================================

export interface CommunicationMapping {
  label: string;
  description?: string;
}

export const FORMALITY_MAPPING: Record<string, CommunicationMapping> = {
  'formalny': {
    label: 'Formalny',
    description: 'Pan/Pani, oficjalne zwroty'
  },
  'semiformalny': {
    label: 'Semiformalny', 
    description: 'Mieszanka formalnych i nieformalnych zwrotów'
  },
  'nieformalny': {
    label: 'Nieformalny',
    description: 'Ty/ty, swobodny styl'
  },
  
  // English fallbacks
  'formal': { label: 'Formalny' },
  'semiformal': { label: 'Semiformalny' },
  'informal': { label: 'Nieformalny' }
} as const;

export const RELATIONSHIP_MAPPING: Record<string, CommunicationMapping> = {
  'oficjalny': {
    label: 'Oficjalny',
    description: 'Profesjonalne, zdystansowane relacje'
  },
  'przyjazny': {
    label: 'Przyjazny',
    description: 'Ciepłe, ale profesjonalne relacje'
  },
  'partnerski': {
    label: 'Partnerski',
    description: 'Równorzędne, współpracujące relacje'
  },
  'bliski': {
    label: 'Bliski',
    description: 'Zażyłe, długoletnie relacje biznesowe'
  },
  
  // English fallbacks
  'official': { label: 'Oficjalny' },
  'friendly': { label: 'Przyjazny' },
  'partnership': { label: 'Partnerski' },
  'close': { label: 'Bliski' }
} as const;

export const TONE_MAPPING: Record<string, CommunicationMapping> = {
  'profesjonalny': {
    label: 'Profesjonalny',
    description: 'Rzeczowy, kompetentny ton'
  },
  'konsultacyjny': {
    label: 'Konsultacyjny', 
    description: 'Doradczy, ekspercki ton'
  },
  'partnerski': {
    label: 'Partnerski',
    description: 'Współpracujący, równorzędny ton'
  },
  'bezpośredni': {
    label: 'Bezpośredni',
    description: 'Jasny, konkretny ton'
  },
  
  // English fallbacks
  'professional': { label: 'Profesjonalny' },
  'consultative': { label: 'Konsultacyjny' },
  'partnership': { label: 'Partnerski' },
  'direct': { label: 'Bezpośredni' }
} as const;

// =============================================================================
// FEEDBACK CATEGORIES MAPPINGS
// =============================================================================

export interface QuestionCategoryMapping {
  label: string;
  description?: string;
  salesStage?: string[];
}

export const QUESTION_CATEGORY_MAPPING: Record<string, QuestionCategoryMapping> = {
  'Pytania Relacyjne': {
    label: 'Pytania Relacyjne',
    description: 'Budowanie relacji i zaufania',
    salesStage: ['Lead Qualification', 'Presentation']
  },
  'Pytania Weryfikacyjne': {
    label: 'Pytania Weryfikacyjne',
    description: 'Sprawdzanie zrozumienia i potwierdzanie informacji'
  },
  'Pytania Odkrywcze': {
    label: 'Pytania Odkrywcze', 
    description: 'Eksploracja potrzeb i wyzwań klienta',
    salesStage: ['Lead Qualification']
  },
  'Pytania Kontrolne': {
    label: 'Pytania Kontrolne',
    description: 'Weryfikacja postępu i zaangażowania',
    salesStage: ['Negotiation', 'Closing']
  },
  'Pytania Zamykające': {
    label: 'Pytania Zamykające',
    description: 'Kierowanie ku decyzji i działaniu',
    salesStage: ['Closing', 'Negotiation']
  }
} as const;

// =============================================================================
// SALES STAGE MAPPINGS
// =============================================================================

export const SALES_STAGE_MAPPING: Record<string, CommunicationMapping> = {
  // Polish stages (new primary values)
  'Kwalifikacja leada': {
    label: 'Kwalifikacja leada',
    description: 'Weryfikacja potrzeb, budżetu, decyzyjności klienta'
  },
  'Prezentacja oferty': {
    label: 'Prezentacja oferty',
    description: 'Przedstawienie rozwiązania, demonstracja wartości'
  },
  'Negocjacje': {
    label: 'Negocjacje',
    description: 'Dyskusja o cenie, warunkach, obiekcjach'
  },
  'Domknięcie sprzedaży': {
    label: 'Domknięcie sprzedaży',
    description: 'Finalizacja transakcji, podpisanie umowy'
  },
  'Upselling/Cross-selling': {
    label: 'Upselling/Cross-selling',
    description: 'Sprzedaż dodatkowych produktów/usług'
  },
  'Zbieranie referencji': {
    label: 'Zbieranie referencji',
    description: 'Pozyskiwanie rekomendacji, case studies'
  },
  // English stages (legacy fallback)
  'Lead Qualification': {
    label: 'Kwalifikacja leada',
    description: 'Weryfikacja potrzeb, budżetu, decyzyjności klienta'
  },
  'Presentation': {
    label: 'Prezentacja oferty',
    description: 'Przedstawienie rozwiązania, demonstracja wartości'
  },
  'Negotiation': {
    label: 'Negocjacje',
    description: 'Dyskusja o cenie, warunkach, obiekcjach'
  },
  'Closing': {
    label: 'Domknięcie sprzedaży',
    description: 'Finalizacja transakcji, podpisanie umowy'
  },
  'Reference Collection': {
    label: 'Zbieranie referencji',
    description: 'Pozyskiwanie rekomendacji, case studies'
  }
} as const;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get priority mapping with fallback
 */
export function getPriorityMapping(priority: string): PriorityMapping {
  return PRIORITY_MAPPING[priority] || {
    label: priority,
    color: 'gray' as const,
    description: 'Nieznany priorytet'
  };
}

/**
 * Get category mapping with fallback  
 */
export function getCategoryMapping(category: string): CategoryMapping {
  return CATEGORY_MAPPING[category] || {
    label: category,
    color: 'gray' as const,
    description: 'Nieznana kategoria'
  };
}

/**
 * Get formality mapping with fallback
 */
export function getFormalityMapping(formality: string): CommunicationMapping {
  return FORMALITY_MAPPING[formality] || {
    label: formality,
    description: 'Nieznany poziom formalności'
  };
}

/**
 * Get relationship mapping with fallback
 */
export function getRelationshipMapping(relationship: string): CommunicationMapping {
  return RELATIONSHIP_MAPPING[relationship] || {
    label: relationship,
    description: 'Nieznany styl relacji'
  };
}

/**
 * Get tone mapping with fallback
 */
export function getToneMapping(tone: string): CommunicationMapping {
  return TONE_MAPPING[tone] || {
    label: tone,
    description: 'Nieznany ton komunikacji'
  };
}

/**
 * Get question category mapping with fallback
 */
export function getQuestionCategoryMapping(category: string): QuestionCategoryMapping {
  return QUESTION_CATEGORY_MAPPING[category] || {
    label: category,
    description: 'Nieznana kategoria pytań'
  };
}

/**
 * Get sales stage mapping with fallback
 */
export function getSalesStageMapping(stage: string): CommunicationMapping {
  return SALES_STAGE_MAPPING[stage] || {
    label: stage,
    description: 'Nieznany etap sprzedaży'
  };
}